/**
 * depositLedger.ts — what drivers paid toward their premiums, one row per payment.
 *
 * THE PAGE READS `insurance_loan_deposits`, AND FOR YEARS IT DID NOT. The table this page was
 * written against was renamed twice, and the name it used to hold — `insurance_loan_deposits` — was
 * then re-used for a NEW table carrying the meaning the page is about. A mechanical rename followed
 * the string rather than the subject, so both tabs re-pointed at `insurance_loan_repayments` and
 * have been stating one row / GHS 5,312.00 where the estate holds 26 rows / GHS 75,052.46. The
 * constraint names on the older table still say `insurance_deposits`; the newer one had to take
 * `_fkey1` because `_fkey` was taken. That is the whole history, and it is why the select below is
 * pinned by a test rather than described.
 *
 * A DEPOSIT AND A REPAYMENT ARE DISJOINT, AND NEITHER PAGE MAY SUM THEM [D-B46]. A deposit is money
 * the driver put toward the premium — it reduces what Primelane advanced and the interest base. A
 * repayment is money paid back against an advance already made. `get_contract_insights` keeps them
 * apart as `deposit_total` and `repayments_total`, and `advanceDebt.ts` says why adding them credits
 * the driver twice. So this module reads deposits and nothing else: the one repayment in the estate
 * belongs beside the advance it discharges, on its own contract's Insurance tab.
 *
 * THE BASIS IS `transactions.transaction_at`, DELIBERATELY UNLIKE PREMIUM YEARS. A deposit is dated
 * when the driver paid it; a premium year is dated by the year it covers, which is why
 * `premiumLedger.ts` buckets on `insurances.starts_at` and `get_contracts_analytics` does too. The
 * two pages are looking at the same money from opposite ends and are NOT meant to reconcile month
 * for month — two of the 26 production deposits land before their own policy starts.
 *
 * LIFTED OUT OF THE COMPONENTS SO IT CAN BE TESTED, on `premiumLedger.ts`' precedent and for its
 * reason: `vitest.config.ts` matches `src/**\/*.test.ts` under `environment: 'node'`, so a rule
 * living inside a `.tsx` queryFn is a rule nothing can reach.
 *
 * ONE BADGE MEANING ON BOTH PAGES. The instrument, its two labels and the three-position filter are
 * imported from `premiumLedger.ts` rather than re-declared — "Paid in full" here and there is one
 * rule (`instrumentFor`, the server's own two limbs) or it is a coincidence waiting to end.
 */

import { supabase } from '@/integrations/supabase/client';
import { instrumentFor, type InsuranceInstrument } from '@/lib/agreements/instrument';
import { driverProfileName } from '@/lib/driverProfile';
import { businessMonthKey } from '@/lib/format';
import { assertComplete } from './premiumLedger';

/**
 * THE REQUEST, VERBATIM — the byte-stability pin, on `PREMIUM_YEARS_SELECT`' pattern.
 *
 * ROOTED ON THE DEPOSIT, so one row in is one row on screen: 26 rows rather than the 308 premium
 * years the sibling page reads, and `count: 'exact'` guards exactly the set the table draws.
 *
 * THE MONEY IS ON THE TRANSACTION. `insurance_loan_deposits` carries no amount of its own — the row
 * is a claim that one transaction paid down one loan — so `transactions(amount, transaction_at)` is
 * both the figure and the date this page is sorted, filtered and bucketed by.
 *
 * `insurance_loan_instalments(id)` IS AN INSTRUMENT LIMB, NOT A DETAIL. `instrumentFor` answers
 * `ADVANCE` for every unknown, so dropping this limb does not fail: it relabels every fully-paid
 * premium as an advance and moves GHS 45,883.96 between two cards with nothing thrown. The test
 * beside this file pins the string and demonstrates that failure.
 *
 * THE JUNCTION CARRIES BOTH THE CONTRACT AND THE POLICY. `insurance_loans` has no FK to
 * `insurances` in the live schema — embedding `insurances(...)` off the loan is a 400 (Sentry
 * FLEET-ADMIN-A) — so the policy year, the vehicle and the driver all come through
 * `insurance_contracts`. Every hop resolves through exactly one foreign key on the live schema, so
 * no PostgREST disambiguation hint is needed.
 *
 * `drivers → profiles` IS THE REVERSE FK AND COMES BACK AS A LIST. `profiles.driver_id` is unique
 * only WHERE `admin_id IS NULL`, so an admin's test profile can sit beside the driver's own row;
 * `admin_id` is in the select because `driverProfileName` needs it to pick the right one.
 */
export const DEPOSIT_LEDGER_SELECT =
    'id, transactions(amount, transaction_at), ' +
    'insurance_loans(id, amount, interest_rate, ' +
    'insurance_loan_instalments(id), ' +
    'insurance_contracts(contract_id, ' +
    'contracts(id, drivers(profiles(id, admin_id, first_name, last_name))), ' +
    'insurances(id, starts_at, expires_at, vehicles(make, model, vehicle_registrations(number)))))';

/** The transaction the deposit claims — the amount, and the day the driver paid it. */
export interface DepositTransactionEmbed {
    amount?: number | null;
    transaction_at?: string | null;
}

/** The vehicle the policy is written over. Its plate is on `vehicle_registrations`, one FK further. */
export interface DepositVehicleEmbed {
    make?: string | null;
    model?: string | null;
    vehicle_registrations?:
        | { number?: string | null }
        | Array<{ number?: string | null }>
        | null;
}

/** The policy — the year it covers, and the vehicle. */
export interface DepositInsuranceEmbed {
    id?: number | null;
    starts_at?: string | null;
    expires_at?: string | null;
    vehicles?: DepositVehicleEmbed | DepositVehicleEmbed[] | null;
}

/** One `profiles` row against the driver — the driver's own, or an admin's test row. */
export interface DepositProfileEmbed {
    id?: number | null;
    admin_id?: number | null;
    first_name?: string | null;
    last_name?: string | null;
}

/** The driver, and every profile row pointing at them. */
export interface DepositDriverEmbed {
    profiles?: DepositProfileEmbed | DepositProfileEmbed[] | null;
}

/** The contract the policy hangs off. */
export interface DepositContractEmbed {
    id?: number | null;
    drivers?: DepositDriverEmbed | DepositDriverEmbed[] | null;
}

/** The junction — the only path from a loan to its contract and to its policy. */
export interface DepositJunctionEmbed {
    contract_id?: number | null;
    contracts?: DepositContractEmbed | DepositContractEmbed[] | null;
    insurances?: DepositInsuranceEmbed | DepositInsuranceEmbed[] | null;
}

/**
 * The loan the deposit is against, as the embed hands it back.
 *
 * SPELLED OUT RATHER THAN `any`, for `PremiumLoanEmbed`'s reason: `amount` and the instalments are
 * the limbs the instrument is derived from, and nothing inside an `any` can be wrong.
 */
export interface DepositLoanEmbed {
    id: number;
    amount?: number | null;
    interest_rate?: number | null;
    insurance_loan_instalments?: Array<{ id: number }> | null;
    insurance_contracts?: DepositJunctionEmbed | DepositJunctionEmbed[] | null;
}

/** One `insurance_loan_deposits` row as the embed hands it back. */
export interface DepositRowEmbed {
    id: number;
    transactions?: DepositTransactionEmbed | DepositTransactionEmbed[] | null;
    insurance_loans?: DepositLoanEmbed | DepositLoanEmbed[] | null;
}

/** One deposit, as the page reads it. */
export interface DepositEntry {
    depositId: number;
    loanId: number | null;
    /** `transactions.amount` — the row's subject, and the only money on it. */
    amount: number;
    /** `transactions.transaction_at` — the day the driver paid, as Postgres stored it. */
    paidAt: string | null;
    /** `A` — `insurance_loans.amount`, the premium this payment is toward. */
    premium: number | null;
    /** `D` — Σ of EVERY deposit on this loan, which is what the instrument is decided on. */
    depositTotal: number;
    /** Σ of this loan's deposits up to and including this one, in payment order. */
    runningTotal: number;
    instalmentCount: number;
    instrument: InsuranceInstrument;
    /** `insurance_loans.interest_rate`, NULL preserved — a rate nobody set is not a rate of zero. */
    rate: number | null;
    contractId: number | null;
    insuranceId: number | null;
    policyStart: string | null;
    policyEnd: string | null;
    driverName: string | null;
    registration: string | null;
    make: string | null;
    model: string | null;
}

/**
 * PostgREST returns a to-one embed as an object and a to-many as an array, and the shape is decided
 * by the REQUEST rather than by the schema — so every to-one embed here accepts both readings,
 * exactly as `premiumLedger.one()` does.
 */
function one<T>(value: T | T[] | null | undefined): T | null {
    if (Array.isArray(value)) return value[0] ?? null;
    return value ?? null;
}

/** One embedded row, flattened — everything except the two figures that need the whole set. */
type DepositFacts = Omit<DepositEntry, 'depositTotal' | 'runningTotal' | 'instrument'>;

function factsFrom(row: DepositRowEmbed): DepositFacts {
    const transaction = one(row.transactions);
    const loan = one(row.insurance_loans);
    const junction = one(loan?.insurance_contracts);
    const contract = one(junction?.contracts);
    const driver = one(contract?.drivers);
    const insurance = one(junction?.insurances);
    const vehicle = one(insurance?.vehicles);
    const registration = one(vehicle?.vehicle_registrations);

    return {
        depositId: row.id,
        loanId: loan?.id ?? null,
        amount: transaction?.amount ?? 0,
        paidAt: transaction?.transaction_at ?? null,
        premium: loan?.amount ?? null,
        instalmentCount: (loan?.insurance_loan_instalments ?? []).length,
        rate: loan?.interest_rate ?? null,
        contractId: junction?.contract_id ?? null,
        insuranceId: insurance?.id ?? null,
        policyStart: insurance?.starts_at ?? null,
        policyEnd: insurance?.expires_at ?? null,
        driverName: driverProfileName(driver?.profiles),
        registration: registration?.number ?? null,
        make: vehicle?.make ?? null,
        model: vehicle?.model ?? null,
    };
}

/**
 * WHICH DEPOSITS BELONG TO ONE PREMIUM. The loan is the group, and a row whose loan embed did not
 * come back is its own group rather than being pooled with every other loanless row — pooling them
 * would invent a deposit total out of unrelated payments.
 */
function groupKeyOf(row: DepositFacts): string {
    return row.loanId === null ? `deposit:${row.depositId}` : `loan:${row.loanId}`;
}

/**
 * Payment order, ascending — the order the running total accumulates in.
 *
 * `transaction_at` THEN `id`, because two deposits on one loan can share a day and a running total
 * that renumbers itself between two renders is not a running total. An undated payment sinks: a
 * deposit nobody dated is not the first one made.
 */
function byPaymentOrder(a: DepositFacts, b: DepositFacts): number {
    if (a.paidAt !== b.paidAt) {
        if (!a.paidAt) return 1;
        if (!b.paidAt) return -1;
        return a.paidAt < b.paidAt ? -1 : 1;
    }
    return a.depositId - b.depositId;
}

/**
 * Every deposit, with its loan's total and its own place in that loan's run.
 *
 * THE PER-LOAN TOTAL IS A GROUP-BY OVER **THIS RESULT SET**, which is correct only while the read is
 * unfiltered and complete. `assertComplete` is what guarantees the second; adding any server-side
 * filter to `fetchDepositLedger` breaks the first, and the instrument would silently flip Records to
 * Advances — a loan whose two deposits together cover the premium reads as half-paid the moment one
 * of them is filtered out. The test beside this file demonstrates that flip rather than describing
 * it. The alternative — embedding the loan's sibling deposits back through the loan — asks the
 * server for the same 26 rows a second time, nested, to answer a question the result set already
 * contains.
 *
 * THE INSTRUMENT IS A PROPERTY OF THE LOAN, NOT OF THE WINDOW. It is derived here, once, off the
 * whole estate; a date window narrows which deposits are COUNTED and never how their loan is
 * classified.
 */
export function depositEntriesFrom(rows: DepositRowEmbed[] | null | undefined): DepositEntry[] {
    const facts = (rows ?? []).map(factsFrom);

    const totals = new Map<string, number>();
    const groups = new Map<string, DepositFacts[]>();
    for (const row of facts) {
        const key = groupKeyOf(row);
        totals.set(key, (totals.get(key) ?? 0) + row.amount);
        const group = groups.get(key);
        if (group) group.push(row);
        else groups.set(key, [row]);
    }

    const running = new Map<number, number>();
    for (const group of groups.values()) {
        let carried = 0;
        for (const row of [...group].sort(byPaymentOrder)) {
            carried += row.amount;
            running.set(row.depositId, carried);
        }
    }

    return facts.map((row) => {
        const depositTotal = totals.get(groupKeyOf(row)) ?? row.amount;
        return {
            ...row,
            depositTotal,
            runningTotal: running.get(row.depositId) ?? row.amount,
            instrument: instrumentFor({
                amount: row.premium,
                depositTotal,
                instalmentCount: row.instalmentCount,
            }),
        };
    });
}

/** The page's cache entry. Both tabs read it, so the whole page is one request. */
export const DEPOSIT_LEDGER_KEY = ['insurance-deposits-ledger'] as const;

/**
 * Every deposit in the estate, in one request.
 *
 * WHOLE-TABLE, AND FOR TWO REASONS RATHER THAN ONE. The instrument needs the loan's COMPLETE
 * deposit set — `SUM(deposits) >= insurance_loans.amount − 0.005`, a correlated aggregate compared
 * against a sibling column, which PostgREST has no `select`/`filter` syntax for — and the "Toward
 * premium" column needs that loan's deposits in date order, which a page of ten rows cannot see.
 * The read this replaces paginated the rows and counted them with a separate `head: true` request
 * sharing no filter: the search box had been dead since it was added, and any filter added to one
 * query and not the other would have made `totalPages` lie.
 *
 * THE CEILING IS THE PROJECT'S API **Max rows** SETTING, WHICH TRUNCATES SILENTLY — not a `pgrst.*`
 * GUC any query can read back. Production holds 26 deposit rows against a default of 1,000, so the
 * headroom is large; past it the answer is pagination or a view exposing the loan's deposit total,
 * not a bigger fetch. `count: 'exact'` is computed over the whole matching set rather than over the
 * rows that came back, which makes `rows.length < count` exactly that truncation — and
 * `assertComplete` turns it into a thrown error on the path both tabs render.
 */
export async function fetchDepositLedger(): Promise<DepositEntry[]> {
    const { data, error, count } = await supabase
        .from('insurance_loan_deposits')
        .select(DEPOSIT_LEDGER_SELECT, { count: 'exact' })
        // Newest first, by the deposit's own id — a stable server order the client sort then
        // refines by payment date. Unordered rows renumber themselves between two renders.
        .order('id', { ascending: false })
        .returns<DepositRowEmbed[]>();
    if (error) throw error;
    const rows = data ?? [];
    // Named, because the guard is shared with the premium-years read and "premium years" on this
    // page would send whoever read the error to the wrong table.
    assertComplete(rows, count, 'insurance deposits');
    return depositEntriesFrom(rows);
}

/**
 * Newest payment first — the order the table's first column is sorted by.
 *
 * TIE-BROKEN ON THE DEPOSIT ID, and an undated payment sinks rather than floating. Sorts a copy;
 * the query cache's array is not the caller's to reorder.
 */
export function sortDepositEntries(rows: DepositEntry[]): DepositEntry[] {
    return [...rows].sort((a, b) => {
        if (a.paidAt !== b.paidAt) {
            if (!a.paidAt) return 1;
            if (!b.paidAt) return -1;
            return a.paidAt < b.paidAt ? 1 : -1;
        }
        return b.depositId - a.depositId;
    });
}

/**
 * What the search box searches — the things the table actually prints, ONE FIELD AT A TIME.
 *
 * PUNCTUATION AND CASE ARE STRIPPED FROM BOTH SIDES, because a Ghanaian plate is stored with spaces
 * ("GW 1769 22") and typed without them as often as with, and a driver's name is typed without its
 * spaces as often as with. The ids are matched the same way, so a `#89` typed straight out of the
 * Contract column finds contract 89. The dates are not searched: a date column is what the sort is
 * for.
 *
 * NOT ONE CONCATENATED HAYSTACK, on `matchesPremiumSearch`' finding: stripping the separators out of
 * a joined string lets a needle straddle two fields that have nothing to do with each other —
 * policy 412 beside contract 89 answers "12489", and a plate's tail beside a make's head answers
 * "22toyota". Neither is anything an admin typed looking for.
 *
 * THE VEHICLE LINE IS ONE FIELD BECAUSE THE PAGE PRINTS IT AS ONE. The Vehicle column draws the
 * plate above `[make, model].join(' ')`, so "Toyota Vitz" is a string an admin reads off the row —
 * and make and model matched only one at a time answer nothing to it. Joining exactly the two parts
 * the cell already joined is not the concatenated haystack above: no needle gains a neighbour it
 * does not have on the page.
 */
function searchableFields(row: DepositEntry): string[] {
    return [
        row.registration,
        row.make,
        row.model,
        [row.make, row.model].filter(Boolean).join(' '),
        row.driverName,
        row.insuranceId === null ? null : String(row.insuranceId),
        row.contractId === null ? null : String(row.contractId),
    ].filter((part): part is string => Boolean(part));
}

const normalise = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

export function matchesDepositSearch(row: DepositEntry, term: string): boolean {
    const needle = normalise(term);
    if (!needle) return true;
    return searchableFields(row).some((field) => normalise(field).includes(needle));
}

/**
 * The payment's Ghana calendar day, as Postgres stored it.
 *
 * READ OFF THE STRING, never through `new Date(...)`. `transactions.transaction_at` is a
 * `timestamptz` that PostgREST serialises in UTC, and UTC is Accra — so the first ten characters
 * ARE the business day, while re-reading the instant in the viewer's zone moves every payment
 * within the offset of a month boundary into the wrong bucket.
 */
export function depositDayOf(row: DepositEntry): string | null {
    return row.paidAt ? row.paidAt.substring(0, 10) : null;
}

/**
 * Rows paid inside an inclusive 'yyyy-MM-dd' window. Undated rows are out.
 *
 * `transactions.transaction_at` is NOT NULL in the live schema, so "undated" only arises from an
 * embed that did not come back — and a payment with no date is in no window, all-time included.
 */
export function withinDepositWindow(row: DepositEntry, fromDate: string, toDate: string): boolean {
    const day = depositDayOf(row);
    return day !== null && day >= fromDate && day <= toDate;
}

/** One month of deposit volume, split by the instrument of the loan each payment is against. */
export interface DepositMonthPoint {
    /** 'yyyy-MM', the Ghana calendar month of `transactions.transaction_at`. */
    monthKey: string;
    /** Σ of that month's payments toward premiums Primelane advanced. */
    towardAdvances: number;
    /** Σ of that month's payments toward premiums the driver paid in full. */
    paidInFull: number;
}

/**
 * The monthly series, on the payment-date basis.
 *
 * TWO SERIES RATHER THAN ONE, so the split the cards state is visible month by month. `monthKeys`
 * comes from the picker's range so an empty month still draws a zero, and a row whose month is
 * outside that list is dropped — the window filter and the buckets agree by construction.
 */
export function depositMonthly(rows: DepositEntry[], monthKeys: string[]): DepositMonthPoint[] {
    const buckets = new Map<string, DepositMonthPoint>();
    for (const monthKey of monthKeys) {
        buckets.set(monthKey, { monthKey, towardAdvances: 0, paidInFull: 0 });
    }
    for (const row of rows) {
        const key = businessMonthKey(row.paidAt);
        const bucket = key ? buckets.get(key) : undefined;
        if (!bucket) continue;
        if (row.instrument === 'PAYMENT_RECORD') bucket.paidInFull += row.amount;
        else bucket.towardAdvances += row.amount;
    }
    return monthKeys.map((monthKey) => buckets.get(monthKey)!);
}

/** What the four cards state. */
export interface DepositSummary {
    /** Σ of every payment in the window. */
    totalDeposited: number;
    depositCount: number;
    /** Σ of the payments against premiums Primelane advanced — they reduce the interest base. */
    towardAdvances: number;
    advanceCount: number;
    /** Σ of the payments against premiums the driver paid in full. */
    paidInFull: number;
    paidInFullCount: number;
    /** The mean payment, over every row in the window. */
    avgDeposit: number;
}

export function depositSummary(rows: DepositEntry[]): DepositSummary {
    const advances = rows.filter((row) => row.instrument === 'ADVANCE');
    const records = rows.filter((row) => row.instrument === 'PAYMENT_RECORD');
    const sum = (entries: DepositEntry[]) => entries.reduce((total, row) => total + row.amount, 0);
    const totalDeposited = sum(rows);

    return {
        totalDeposited,
        depositCount: rows.length,
        towardAdvances: sum(advances),
        advanceCount: advances.length,
        paidInFull: sum(records),
        paidInFullCount: records.length,
        avgDeposit: rows.length > 0 ? totalDeposited / rows.length : 0,
    };
}

/**
 * How far this payment carried the premium — the fraction, unrounded, or null.
 *
 * A FRACTION RATHER THAN A FORMATTED PERCENTAGE, because rounding belongs at display: the cell
 * renders `Math.round(share * 100)` beside the two figures it is the ratio of, and a reader who
 * wants the pesewas reads them there. NULL where there is no premium to be a share OF — a loan the
 * embed did not return, or a premium of zero, whose share would be an infinity or a NaN.
 */
export function depositShare(row: DepositEntry): number | null {
    if (row.premium === null || !(row.premium > 0)) return null;
    return row.runningTotal / row.premium;
}
